import { useEffect, useState } from "react";
import { Check, Layers } from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";

/**
 * Single-select Category picker for TimerBar. `value` is an array (kept for
 * backend compat) but only the first id is used; `onChange(next[])` receives
 * an array with 0 or 1 ids.
 */
export default function TimerTagPicker({ tags, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => { if (open) setSearch(""); }, [open]);
  const currentId = (value && value[0]) || null;
  const selected = currentId ? tags.find((t) => t.id === currentId) : null;
  const query = search.trim().toLowerCase();
  const filtered = tags.filter((t) => !query || t.name.toLowerCase().includes(query));
  const pick = (id) => {
    onChange(id === currentId ? [] : [id]);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 px-2 rounded-md hover:bg-slate-100 text-slate-600 flex items-center gap-1 text-xs max-w-[260px]"
          data-testid="timer-tag-picker"
        >
          <Layers className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          {!selected ? (
            <span className="text-slate-400">Category</span>
          ) : (
            <span key={selected.id}
                  className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                  style={{ background: `${selected.color}1a`, color: selected.color }}
                  data-testid={`timer-tag-pill-${selected.id}`}>
              {selected.name}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="end">
        <input value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder="Filter categories…"
               className="w-full text-sm px-2 py-1.5 rounded-md border border-slate-200 focus:border-[#145c8c] outline-none mb-2"
               data-testid="timer-tag-search" />
        <div className="max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center">
              {tags.length === 0 ? "No categories yet — create one on the Categories page." : "No matches."}
            </div>
          ) : filtered.map((t) => {
            const isOn = currentId === t.id;
            return (
              <button
                key={t.id}
                onClick={() => pick(t.id)}
                className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm ${isOn ? "bg-slate-50" : ""}`}
                data-testid={`timer-tag-option-${t.id}`}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />
                <span className="flex-1 truncate">{t.name}</span>
                {isOn && <Check className="h-3.5 w-3.5 text-[#145c8c]" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
